import { Form, Grid, Input, Tooltip } from '@mairistem/composants';

import { useAutofocus } from '@mairistem/comptabilite';
import type { ErrorField } from '@mairistem/fiche/lib/ts/src/types';
import {
	Combobox,
	Date,
	EtiquettesInput,
	type TemplateRequestGet,
} from '@mairistem/finances-utils';
import { request } from '@mairistem/tools';
import React from 'react';
import { toast } from 'react-toastify';
import Avatar from '@/src/components/Common/Avatar';
import ProjetComposant from '@/src/components/ProjetFichePanel/ProjetComposant/ProjetComposant';
import UtilisateurProjetInput from '@/src/components/ProjetFichePanel/UtilisateurProjetInput/UtilisateurProjetInput';
import { PROJET_STATUS_OPTIONS } from '@/src/constants/projetStatus';
import type { Projet } from '@/src/interfaces/projet';
import type { Service } from '@/src/interfaces/service';
import { getTooltipError, isError } from '@/src/utils/tools';

type PickedProjetKeys =
	| 'identifiant'
	| 'libelle'
	| 'montantHT'
	| 'montantTVA'
	| 'montantTTC'
	| 'dateFin'
	| 'identifiantCodeTVA'
	| 'etiquettes'
	| 'dateCreation'
	| 'dateExecution'
	| 'status'
	| 'identifiantDocInfo'
	| 'referenceInterne'
	| 'status'
	| 'identifiantEntite'
	| 'utilisateursProjet'
	| 'composants'
	| 'deletedComposants'
	| 'deletedUtilisateursProjet'
	| 'identifiantService';

interface ProjetInformationProps {
	projet: Pick<Projet, PickedProjetKeys>;
	onChange: (any) => void;
	errors: Array<ErrorField>;
}

const ProjetInformation = ({
	projet = null,
	onChange = null,
	errors,
}: ProjetInformationProps) => {
	const mainInput = useAutofocus([projet?.identifiant]);
	const [services, setServices] = React.useState<Service[]>([]);

	const handleChange = (
		_e,
		{ name, value }: { name: string; value: string | number | boolean },
	) => {
		if (onChange) {
			onChange({ [name]: value });
		}
	};

	const handleChangeUserService = (
		_e,
		{ name, value }: { name: string; value: string | number | boolean },
	) => {
		if (onChange) {
			onChange({ [name]: value === '' ? null : value });
		}
	};

	const handleChangeDate = (value: string, name: string) =>
		onChange?.({ [name]: value });

	React.useEffect(() => {
		request
			.get<unknown, TemplateRequestGet<Service>>(
				'/api/finances/comptabilite/piecescomptables/services/getServicesAccordingToRole',
			)
			.then(({ data }) => {
				setServices(data);
			})
			.catch(() => {
				toast.error(
					'Erreur lors de la récupération des services utilisateurs.',
				);
			});
	}, []);

	return (
		<Form>
			<Grid stackable>
				<Grid.Row columns={2}>
					<Grid.Column width={12}>
						<Form.Group>
							<Tooltip
								{...getTooltipError(
									errors,
									'subventionsPercues-referenceDossier',
								)}
								trigger={
									<Form.Field
										label="Intitulé"
										type="text"
										// @ts-expect-error
										control={Input}
										placeholder="Intitulé du projet"
										name="libelle"
										value={projet?.libelle ?? ''}
										required
										error={isError(
											errors,
											'subventionsPercues-referenceDossier',
										)}
										onChange={handleChange}
										fluid
										debounced
										width={16}
										input={{ ref: mainInput, 'data-testid': 'libelleProjet' }}
									/>
								}
							/>
						</Form.Group>

						<Form.Field
							label="Référence interne"
							type="text"
							placeholder="Référence interne"
							name="referenceInterne"
							// @ts-expect-error
							control={Input}
							value={projet?.referenceInterne ?? ''}
							onChange={handleChange}
							fluid
							width={16}
							input={{ 'data-testid': 'referenceInterneProjet' }}
						/>

						<UtilisateurProjetInput
							onChange={onChange}
							utilisateursProjet={projet?.utilisateursProjet}
							deletedUtilisateursProjet={projet?.deletedUtilisateursProjet}
							identifiant={projet?.identifiant}
						/>

						<Form.Field
							// @ts-expect-error
							name="status"
							control={Combobox}
							label="Stade"
							placeholder="Stade"
							fluid
							search
							selection
							value={projet?.status ?? null}
							options={PROJET_STATUS_OPTIONS}
							data-testid="statusProjetDropdown"
							onChange={handleChange}
							required
						/>

						<Form.Group>
							<Form.Field
								label="Date de création"
								width={7}
								control={Date}
								// @ts-expect-error
								name="dateCreation"
								value={projet?.dateCreation}
								onChange={handleChangeDate}
								fluid
								data-testid="dateCreationProjet"
							/>

							<Form.Field
								label="Commencement d'exécution"
								control={Date}
								width={4}
								// @ts-expect-error
								name="dateExecution"
								value={projet?.dateExecution}
								onChange={handleChangeDate}
								fluid
								data-testid="dateExecutionProjet"
							/>

							<Tooltip
								{...getTooltipError(errors, 'subventionspercues-dateFin')}
								trigger={
									<Form.Field
										control={Date}
										// @ts-expect-error
										name="dateFin"
										label="Achèvement de l'opération"
										value={projet?.dateFin}
										onChange={handleChangeDate}
										fluid
										width={5}
										data-testid="dateFinProjet"
									/>
								}
							/>
						</Form.Group>

						<EtiquettesInput
							baseUrl="/api/finances/subventionsPercues/etiquettes"
							identifiantEntite={projet?.identifiantEntite}
							onChange={onChange}
							selectedEtiquettes={projet?.etiquettes}
							allowAdditions
						/>
						{services.length > 0 && (
							<Form.Field
								// @ts-expect-error
								name="identifiantService"
								control={Combobox}
								label="Service utilisateur"
								placeholder="Service utilisateur"
								fluid
								selection
								clearable
								value={projet?.identifiantService ?? null}
								options={services?.map((service) => ({
									key: service.identifiant,
									text: service.libelle,
									value: service.identifiant,
								}))}
								data-testid="userService"
								onChange={handleChangeUserService}
							/>
						)}
					</Grid.Column>

					<Grid.Column width={4}>
						<div className="avatarWrapper">
							<Avatar
								identifiant={projet?.identifiant}
								identifiantDocInfo={projet?.identifiantDocInfo}
								handleChange={onChange}
							/>
						</div>
					</Grid.Column>
				</Grid.Row>
				<Grid.Row>
					<Grid.Column>
						<ProjetComposant
							composants={projet?.composants}
							deletedComposants={projet?.deletedComposants}
							onChange={onChange}
							status={projet?.status}
						/>
					</Grid.Column>
				</Grid.Row>
			</Grid>
		</Form>
	);
};

export default ProjetInformation;
